import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { AnyObject } from 'yup/lib/types';
import { isEqual } from 'lodash';

import { DataState } from '../../components/ProjectManagement/Notes/interface/notes.interface';

export const defaultSectionsUrlParams = {
  limit: 10,
  page: 1,
  sortBy: 'section_id',
  sortOrder: 'DESC',
};
export const defaultNotesUrlParams = {
  limit: 10,
  page: 1,
  sortBy: 'projectNoteId',
  sortOrder: 'DESC',
};
const initialState: DataState = {
  sectionsUrlParams: { ...defaultSectionsUrlParams },
  isSectionPaginationLoading: false,
  isNotePaginationLoading: false,
  projectSectionData: {
    count: 0,
    sections: [],
    newSection: [],
  },
  notesUrlParams: { ...defaultNotesUrlParams },
  projectNotesData: {
    count: 0,
    notes: [],
    newMessage: [],
  },
  selectedSection: {},
  isNewChat: false,
  isSectionLoading: 0,
  isNoteLoading: 0,
  noteQueue: [],
  sectionQueue: [],
  isFirstTimeRenderCompleted: false,
};

const projectNotesSlice = createSlice({
  name: 'projectNotes',
  initialState,
  reducers: {
    handleGetSectionsResponse(state, action: AnyObject) {
      const data = action?.payload?.data;
      if (data?.count) {
        if (Array.isArray(data?.sections) && data.sections.length) {
          const previousSections = [];
          let isAnyOpen = false;
          let closedSection: AnyObject | null = null;
          for (let index = data.sections.length - 1; index >= 0; index--) {
            const eachSection = {
              ...data.sections[index],
              title: dayjs(new Date(data.sections[index].created_at)).format(
                'MMM DD, YYYY hh:mm A'
              ),
            };
            previousSections.push(eachSection);
          }
          const uniqueSections = new Map();
          if (state.isSectionPaginationLoading) {
            previousSections.forEach(section => {
              if (!section.is_closed) isAnyOpen = true;
              if (section.section_id === state.selectedSection?.section_id)
                closedSection = section;
              uniqueSections.set(section.section_id, section);
            });
            state.projectSectionData.sections.forEach(section => {
              if (!section.is_closed) isAnyOpen = true;
              if (section.section_id === state.selectedSection?.section_id)
                closedSection = section;
              uniqueSections.set(section.section_id, section);
            });
            const newSectionData = {
              count: data.count,
              sections: Array.from(uniqueSections.values()),
              newSection: [],
            };
            if (!isEqual(newSectionData, state.projectSectionData)) {
              state.projectSectionData = newSectionData;
            }
            /* state.projectSectionData = {
              count: data.count,
              sections: Array.from(uniqueSections.values()),
              newSection: [],
            }; */
          } else {
            const uniqueSections = new Map();
            if (
              state.projectSectionData.count &&
              data?.count !== state.projectSectionData.count
            ) {
              const prevUniqueSections = new Map();
              state.projectSectionData.sections.forEach(section => {
                if (!section.is_closed) isAnyOpen = true;
                if (section.section_id === state.selectedSection?.section_id)
                  closedSection = section;
                uniqueSections.set(section.section_id, section);
              });
              previousSections.forEach(section => {
                if (!section.is_closed) isAnyOpen = true;
                prevUniqueSections.set(section.section_id, section);
              });
              const newSectionData = {
                count: data.count,
                sections: Array.from(uniqueSections.values()),
                newSection: Array.from(prevUniqueSections.values()),
              };
              if (!isEqual(newSectionData, state.projectSectionData)) {
                state.projectSectionData = newSectionData;
              }
              /* state.projectSectionData = {
                count: data.count,
                sections: Array.from(uniqueSections.values()),
                newSection: Array.from(prevUniqueSections.values()),
              }; */
            } else {
              state.projectSectionData.sections.forEach(section => {
                if (!section.is_closed) isAnyOpen = true;
                if (section.section_id === state.selectedSection?.section_id)
                  closedSection = section;
                uniqueSections.set(section.section_id, section);
              });
              previousSections.forEach(section => {
                if (!section.is_closed) isAnyOpen = true;
                if (section.section_id === state.selectedSection?.section_id)
                  closedSection = section;
                uniqueSections.set(section.section_id, section);
              });
              const newSectionData = {
                count: data.count,
                sections: Array.from(uniqueSections.values()),
                newSection: state.projectSectionData.newSection.length
                  ? Array.from(uniqueSections.values())
                  : [],
              };
              if (!isEqual(newSectionData, state.projectSectionData)) {
                state.projectSectionData = newSectionData;
              }
              /* state.projectSectionData = {
                count: data.count,
                sections: Array.from(uniqueSections.values()),
                newSection: state.projectSectionData.newSection.length
                  ? Array.from(uniqueSections.values())
                  : [],
              }; */
            }
          }
          if (!state.selectedSection?.section_id && !state.isNewChat) {
            state.selectedSection =
              state.projectSectionData.sections[
                state.projectSectionData.sections.length - 1
              ];
          } else if (closedSection) {
            state.selectedSection = closedSection;
          }
          if (!isAnyOpen) state.isNewChat = false;
          else if (isAnyOpen && !state.isNewChat) state.isNewChat = true;
        }
      } else if (data?.count === 0 && !state.isNewChat) {
        state.projectSectionData = {
          count: 0,
          sections: [],
          newSection: [],
        };
      }
    },
    handleCloseSectionResponse(state) {
      state.projectSectionData = {
        count: state.projectSectionData.count,
        sections: state.projectSectionData.sections.map(section => {
          if (section.section_id === state.selectedSection?.section_id) {
            return { ...section, is_closed: true };
          }
          return section;
        }),
        newSection: state.projectSectionData.newSection,
      };
      state.selectedSection = {
        ...state.selectedSection,
        is_closed: true,
      };
      state.isNewChat = false;
    },
    handleGetSectionNotesResponse(state, action: AnyObject) {
      const data = action?.payload?.data;
      if (data?.count) {
        if (Array.isArray(data?.notes) && data.notes.length) {
          const previousNotes = [];
          for (let index = data.notes.length - 1; index >= 0; index--) {
            previousNotes.push(data.notes[index]);
          }
          if (state.isNotePaginationLoading) {
            const uniqueNotes = new Map();
            previousNotes.forEach(note => {
              uniqueNotes.set(note.projectNoteId, note);
            });
            state.projectNotesData.notes.forEach(note => {
              uniqueNotes.set(note.projectNoteId, note);
            });
            const newNoteData = {
              count: data.count,
              notes: Array.from(uniqueNotes.values()),
              newMessage: state.projectNotesData.newMessage,
            };
            if (!isEqual(newNoteData, state.projectNotesData)) {
              state.projectNotesData = newNoteData;
            }
            /* state.projectNotesData = {
              count: data.count,
              notes: Array.from(uniqueNotes.values()),
              newMessage: state.projectNotesData.newMessage,
            }; */
          } else {
            const uniqueNotes = new Map();
            if (
              state.projectNotesData.count &&
              data?.count !== state.projectNotesData.count
            ) {
              const prevUniqueNotes = new Map();
              state.projectNotesData.notes.forEach(note => {
                uniqueNotes.set(note.projectNoteId, note);
              });
              previousNotes.forEach(note => {
                prevUniqueNotes.set(note.projectNoteId, note);
              });
              const newNoteData = {
                count: data.count,
                notes: Array.from(uniqueNotes.values()),
                newMessage: Array.from(prevUniqueNotes.values()),
              };
              if (!isEqual(newNoteData, state.projectNotesData)) {
                state.projectNotesData = newNoteData;
              }
              /* state.projectNotesData = {
                count: data.count,
                notes: Array.from(uniqueNotes.values()),
                newMessage: Array.from(prevUniqueNotes.values()),
              }; */
            } else {
              state.projectNotesData.notes.forEach(note => {
                uniqueNotes.set(note.projectNoteId, note);
              });
              previousNotes.forEach(note => {
                uniqueNotes.set(note.projectNoteId, note);
              });
              const newNoteData = {
                count: data.count,
                notes: Array.from(uniqueNotes.values()),
                newMessage: state.projectNotesData.newMessage.length
                  ? Array.from(uniqueNotes.values())
                  : [],
              };
              if (!isEqual(newNoteData, state.projectNotesData)) {
                state.projectNotesData = newNoteData;
              }
              /* state.projectNotesData = {
                count: data.count,
                notes: Array.from(uniqueNotes.values()),
                newMessage: state.projectNotesData.newMessage.length
                  ? Array.from(uniqueNotes.values())
                  : [],
              }; */
            }
          }
        }
      } else if (!state.isNewChat) {
        state.projectNotesData = {
          count: 0,
          notes: [],
          newMessage: [],
        };
      }
    },
    handleCreateSectionNoteResponse(state, action: AnyObject) {
      const data = action?.payload?.data;
      if (data?.Section?.section_id) {
        const previousNotes = state.projectNotesData.notes.slice(
          0,
          state.projectNotesData.notes.length - state.noteQueue.length
        );
        const remainingQueueNotes = state.noteQueue.slice(1);
        state.projectNotesData = {
          count: state.projectNotesData.count,
          notes: [...previousNotes, data, ...remainingQueueNotes],
          newMessage: state.projectNotesData.newMessage,
        };
        if (state.selectedSection?.is_new) {
          state.projectSectionData = {
            count: state.projectSectionData.count,
            sections: state.projectSectionData.sections.map(each => {
              if (each.section_id === state.selectedSection?.section_id) {
                return {
                  section_id: data?.Section?.section_id,
                  title: dayjs(new Date(data?.Section?.created_at)).format(
                    'MMM DD, YYYY hh:mm A'
                  ),
                  is_closed: data?.Section?.is_closed,
                };
              }
              return each;
            }),
            newSection: state.projectSectionData.newSection,
          };
          state.selectedSection = {
            ...state.selectedSection,
            section_id: data?.Section?.section_id,
            is_new: undefined,
          };
        }
      }
      if (state.noteQueue.length) {
        state.noteQueue = state.noteQueue.slice(1);
      }
    },
    handleUpdateSectionNoteResponse(state) {
      state.projectNotesData = {
        count: state.projectNotesData.count,
        notes: state.projectNotesData.notes.map((msg: AnyObject) => {
          if (msg.wait) {
            return {
              ...msg,
              wait: undefined,
            };
          }
          return msg;
        }),
        newMessage: state.projectNotesData.newMessage,
      };
    },
    handleDeleteSectionNoteResponse(state) {
      state.projectNotesData = {
        count: state.projectNotesData.count,
        notes: state.projectNotesData.notes.filter(
          (msg: AnyObject) => !msg?.isDeleted
        ),
        newMessage: state.projectNotesData.newMessage,
      };
    },
    setSelectedSection(state, action) {
      if (action.payload?.isAction) {
        delete action.payload.isAction;
        state.selectedSection = action.payload;
      } else {
        state.selectedSection = action.payload;
        state.projectNotesData = {
          count: 0,
          notes: [],
          newMessage: [],
        };
      }
    },
    setSectionUrlParams(state, action) {
      state.sectionsUrlParams = {
        ...state.sectionsUrlParams,
        ...action.payload,
      };
    },
    setSectionList(state, action) {
      state.projectSectionData = action.payload;
    },
    setNoteUrlParams(state, action) {
      state.notesUrlParams = {
        ...state.notesUrlParams,
        ...action.payload,
      };
    },
    setNotesList(state, action) {
      state.projectNotesData = action.payload;
    },
    setIsNewChat(state, action) {
      state.isNewChat = action.payload;
      state.projectNotesData = {
        count: 0,
        notes: [],
        newMessage: [],
      };
      if (action.payload) {
        const id = +new Date();
        const newSection = {
          section_id: id,
          title: dayjs().format('MMM DD, YYYY hh:mm A'),
          is_closed: false,
          is_new: true,
        };
        state.projectSectionData = {
          count: (state.projectSectionData?.count || 0) + 1,
          sections: Array.isArray(state.projectSectionData?.sections)
            ? [
                ...state.projectSectionData.sections.map(e => ({
                  ...e,
                  is_new: false,
                })),
                newSection,
              ]
            : [newSection],
          newSection: [],
        };
        state.selectedSection = newSection;
      } else {
        if (state.projectSectionData.count) {
          state.projectSectionData = {
            count: state.projectSectionData.count - 1,
            sections: state.projectSectionData.sections.filter(e => !e.is_new),
            newSection: [],
          };
        }
        const remainingLength = state.projectSectionData.sections.length;
        if (remainingLength) {
          state.selectedSection =
            state.projectSectionData.sections[remainingLength - 1];
        } else state.selectedSection = {};
      }
    },
    setSectionLoader(state, action) {
      state.isSectionLoading = action.payload;
    },
    setNoteLoader(state, action) {
      state.isNoteLoading = action.payload;
    },
    setNoteQueue(state, action) {
      state.noteQueue = action.payload;
    },
    setFirstTimeRender(state, action) {
      state.isFirstTimeRenderCompleted = action.payload;
    },
    setSectionPaginationLoading(state, action) {
      state.isSectionPaginationLoading = action.payload;
    },
    setNotePaginationLoading(state, action) {
      state.isNotePaginationLoading = action.payload;
    },
  },
});
export const {
  handleGetSectionsResponse,
  handleCloseSectionResponse,
  handleGetSectionNotesResponse,
  handleCreateSectionNoteResponse,
  handleUpdateSectionNoteResponse,
  handleDeleteSectionNoteResponse,
  setSelectedSection,
  setSectionList,
  setNotesList,
  setIsNewChat,
  setSectionUrlParams,
  setNoteUrlParams,
  setSectionLoader,
  setNoteLoader,
  setNoteQueue,
  setFirstTimeRender,
  setSectionPaginationLoading,
  setNotePaginationLoading,
} = projectNotesSlice.actions;

export const projectNotesSliceReducer = projectNotesSlice.reducer;
